import { FALLBACK_RATE } from './currency.types';

/**
 * Custom-piece Order / Offer / Project domain types.
 *
 * Naming:
 *   BuyerOrder  — buyer's custom-piece request    (was "Commission")
 *   DesignerOffer — designer's priced proposal    (was "Quote")
 *   Project     — active production record
 *
 * NOTE: `orders.types.ts` is reserved for product/shop orders.
 *       Custom commission/brief types live here.
 */

export type BuyerOrderStatus =
  | 'submitted'
  | 'matching'
  | 'offers_open'
  | 'accepted'
  | 'cancelled';

export type DesignerOfferStatus =
  | 'pending'
  | 'submitted'
  | 'countered'
  | 'revised'
  | 'accepted'
  | 'declined'
  | 'withdrawn'
  | 'expired';

export interface CounterOffer {
  askedAtISO: string;
  message: string;
  proposedTotal?: number;
  proposedLeadTimeDays?: number;
}

export type ExtensionStatus = 'pending' | 'accepted' | 'rejected';
export interface ExtensionRequest {
  proposedEtaISO: string;
  reason: string;
  status: ExtensionStatus;
  requestedAtISO: string;
  respondedAtISO?: string;
}

export type ProjectStatus =
  | 'awaiting_deposit'
  | 'in_production'
  | 'ready_to_ship'
  | 'shipped'
  | 'delivered'
  | 'completed'
  | 'disputed'
  | 'refunded';

export type MilestoneKey = 'deposit' | 'completion';
export type MilestoneState = 'due' | 'paid' | 'released' | 'refunded';

export interface BriefRef {
  type: 'image' | 'link';
  src: string;
}

export interface OrderBrief {
  occasion: string;
  occasionId: string;
  monthLabel: string;
  eventDate?: string;
  note?: string;
  styleRefs: BriefRef[];
  fabricChoice: 'have' | 'source' | '';
  fabricImages: string[];
}

export interface ContactInfo {
  fullName: string;
  email: string;
  phone: string;
  phoneIsWhatsapp: boolean;
  /** Buyer-supplied location as a single free-text string, e.g. "Lagos, Nigeria". */
  location?: string;
  /** IP-derived city (from rfq_orders.iplocation), if the backend populated it. */
  city?: string;
  /** IP-derived country name (from rfq_orders.iplocation), if populated. */
  country?: string;
  /** ISO-2 country code (e.g. "NG"), used for the flag emoji. */
  countryCode?: string;
}

export interface DesignerSummary {
  id: string;
  name: string;
  handle: string;
  avatar: string;
  city: string;
  verified: boolean;
  rating: number;
  reviewCount: number;
  portfolio: string[];
}

/** Designer's response state on a brief they were matched to, if the backend supplies it. */
export type MatchedDesignerStatus = 'pending' | 'viewed' | 'accepted' | 'passed' | 'offer_sent';

/** A designer the backend's matching logic routed a buyer's brief to. */
export interface MatchedDesigner extends DesignerSummary {
  status?: MatchedDesignerStatus;
}

export interface OfferBreakdown {
  designFee: number;
  fabricEstimate: number;
  delivery: number;
}

/** Designer's priced proposal on a buyer's custom-piece request. */
export interface DesignerOffer {
  id: string;
  orderId: string;
  designer: DesignerSummary;
  status: DesignerOfferStatus;
  total: number;
  breakdown: OfferBreakdown;
  leadTimeDays: number;
  readyByISO: string;
  inclusions: string[];
  pitch: string;
  submittedAtISO: string;
  expiresAtISO: string;
  threadId: string;
  editedAtISO?: string;
  withdrawnAtISO?: string;
  declinedReason?: string;
  counter?: CounterOffer;
}

/** How a designer was put on a brief (from their rfq_order_designers row). */
export type MatchSource = 'auto_matched' | 'manual' | 'direct_booking';

/** Buyer's custom-piece request. */
export interface BuyerOrder {
  id: string;
  buyerId: string;
  status: BuyerOrderStatus;
  brief: OrderBrief;
  contact: ContactInfo;
  createdAtISO: string;
  acceptedOfferId?: string;
  projectId?: string;
  /**
   * On a designer's incoming brief: how they landed on it. 'direct_booking'
   * means the buyer used this designer's personal order link. Undefined on
   * buyer-side reads. From the backend's `match_source`.
   */
  matchSource?: MatchSource;
  /** How the brief originated overall. From the backend order's `source`. */
  source?: 'matched' | 'direct_booking';
  /** This designer's response status on the brief (from rfq_order_designers row). */
  designerStatus?: MatchedDesignerStatus;
}

export interface Milestone {
  key: MilestoneKey;
  label: string;
  amount: number;
  state: MilestoneState;
  paidAtISO?: string;
  releasedAtISO?: string;
}

export interface ProjectUpdate {
  id: string;
  authorId: string;
  authorName: string;
  authorAvatar: string;
  body: string;
  media: string[];
  createdAtISO: string;
}

export type TimelineKey =
  | 'brief_accepted'
  | 'deposit_paid'
  | 'in_production'
  | 'final_fitting'
  | 'ready_to_ship'
  | 'shipped'
  | 'delivered'
  | 'completed';

export interface TimelineStep {
  key: TimelineKey;
  label: string;
  doneAtISO?: string;
  etaISO?: string;
  active: boolean;
  done: boolean;
}

export interface Project {
  id: string;
  orderId: string;
  buyerId: string;
  designer: DesignerSummary;
  status: ProjectStatus;
  total: number;
  protectionFee: number;
  milestones: Milestone[];
  timeline: TimelineStep[];
  updates: ProjectUpdate[];
  threadId: string;
  shippedTracking?: { carrier: string; ref: string };
  createdAtISO: string;
  etaISO: string;
  autoReleaseAtISO?: string;
  deliveredByDesignerAtISO?: string;
  extensionRequest?: ExtensionRequest;
}

export interface OfferTemplate {
  id: string;
  name: string;
  designFee: number;
  fabricEstimate: number;
  delivery: number;
  leadTimeDays: number;
  inclusionsText: string;
  pitch: string;
  createdAtISO: string;
}

export const PROTECTION_FEE_RATE = 0.02;
export const PROTECTION_FEE_FLAT = 500;
export const DEPOSIT_RATE = 0.7;

export function computePricing(total: number) {
  const protectionFee = Math.round(total * PROTECTION_FEE_RATE) + PROTECTION_FEE_FLAT;
  const grand = total + protectionFee;
  const depositDue = Math.round(grand * DEPOSIT_RATE);
  const completionDue = grand - depositDue;
  return { total, protectionFee, grand, depositDue, completionDue };
}

/**
 * Module-level currency state — synced from the Redux currency slice via
 * store.subscribe(). Allows `formatMoney()` to be currency-aware without
 * requiring every caller to use a hook.
 */
type CurrencyState = {
  currency: 'NGN' | 'USD';
  ngnPerUsd: number;
};

// Seed from the shared FALLBACK_RATE so the two constants can't drift.
let _currencyState: CurrencyState = {
  currency: 'NGN',
  ngnPerUsd: FALLBACK_RATE.ngnPerUsd,
};

/** Called by store.subscribe() whenever currency or rate changes. */
export function setActiveCurrency(state: CurrencyState): void {
  _currencyState = state;
}

/**
 * Format an NGN-stored amount in the user's currently-selected display
 * currency. The provider keeps `_currencyState` synced; non-React code
 * (services, utils, child components) can call this without a hook.
 */
export function formatMoney(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) {
    return _currencyState.currency === 'USD' ? '$0' : '₦0';
  }
  const ngn = Number(n);
  if (_currencyState.currency === 'USD') {
    const rate = _currencyState.ngnPerUsd > 0 ? _currencyState.ngnPerUsd : FALLBACK_RATE.ngnPerUsd;
    const usd = ngn / rate;
    return `$${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `₦${ngn.toLocaleString('en-NG')}`;
}

/**
 * @deprecated Use `formatMoney` instead. Kept as an alias so the existing
 * call sites keep working. The function is currency-aware — it formats in
 * the user's selected display currency, not always NGN.
 */
export const formatNaira = formatMoney;

/**
 * A previewable invoice for one designer's offer — reference, line items,
 * and the full deposit/balance split. Built client-side from existing offer
 * + order fields so it can be shown the moment an offer is submitted, with
 * no extra backend round-trip.
 */
export interface InvoiceData {
  reference: string;
  issuedAtISO: string;
  designer: DesignerSummary;
  buyerName: string;
  orderRef: string;
  breakdown: OfferBreakdown;
  total: number;
  pricing: ReturnType<typeof computePricing>;
  leadTimeDays: number;
  readyByISO: string;
  inclusions: string[];
}

export function buildInvoice(offer: DesignerOffer, order: BuyerOrder): InvoiceData {
  return {
    reference: `INV-${offer.id.slice(0, 8).toUpperCase()}`,
    issuedAtISO: offer.submittedAtISO,
    designer: offer.designer,
    buyerName: order.contact.fullName,
    orderRef: `${order.brief.occasion} · ${order.brief.eventDate ? new Date(order.brief.eventDate).toLocaleDateString('en', { month: 'long', day: 'numeric', year: 'numeric' }) : order.brief.monthLabel}`,
    breakdown: offer.breakdown,
    total: offer.total,
    pricing: computePricing(offer.total),
    leadTimeDays: offer.leadTimeDays,
    readyByISO: offer.readyByISO,
    inclusions: offer.inclusions,
  };
}

// ── Backward-compat aliases (used throughout the codebase) ───────────
export type Commission = BuyerOrder;
export type CommissionStatus = BuyerOrderStatus;
export type CommissionBrief = OrderBrief;
export type Quote = DesignerOffer;
export type QuoteStatus = DesignerOfferStatus;
export type QuoteBreakdown = OfferBreakdown;
// Buyer-side aliases
export type Order = BuyerOrder;
export type Offer = DesignerOffer;
